/**
 * The Accessibility tab's issues-by-impact donut (resources/views/components/accessibility/impact-card.blade.php):
 * draws its wedges again when a legend pill hides or shows a severity, and says the hovered or focused severity's
 * count and share in its middle. The parts and every word come from App\Services\BlinkPages\Accessibility\ImpactDonut;
 * this does only the arithmetic the hidden set changes.
 */

/** The donut's length: 2πr for its radius of 58.5 in a 200-unit box. */
export const DONUT_LENGTH = 2 * Math.PI * 58.5;

/** The white between two wedges (the dataviz surface gap), and the least a severity with issues is drawn at. */
const GAP = 2;
const LEAST = 1;

/**
 * Each part's wedge on a donut that starts at the top, keyed by the part's key: its dash, its offset and its whole
 * percent of the issues shown. The shown parts close the donut between them and a hidden one draws nothing; a part
 * shown alone draws the whole ring, with no gap to set it apart from.
 */
export function wedges(parts, hidden) {
    const shown = parts.filter((part) => !hidden[part.key]);
    const total = shown.reduce((sum, part) => sum + part.count, 0);
    const gap = shown.length > 1 ? GAP : 0;
    const ring = DONUT_LENGTH.toFixed(2);
    let start = 0;

    return Object.fromEntries(parts.map((part) => {
        const drawn = !hidden[part.key] && total > 0;
        const length = drawn ? (DONUT_LENGTH * part.count) / total : 0;
        const wedge = {
            dash: `${(drawn ? Math.max(length - gap, LEAST) : 0).toFixed(2)} ${ring}`,
            offset: (-start).toFixed(2),
            share: drawn ? Math.round((100 * part.count) / total) : 0,
        };
        start += length;

        return [part.key, wedge];
    }));
}

export default (parts, words) => ({
    parts,
    hidden: {},
    // The severity whose wedge, or pill, the pointer or focus is on.
    active: null,

    get wedges() {
        return wedges(this.parts, this.hidden);
    },

    get shownTotal() {
        return this.parts.reduce((sum, part) => sum + (this.hidden[part.key] ? 0 : part.count), 0);
    },

    isShown(key) {
        return !this.hidden[key];
    },

    isFaded(key) {
        return this.active !== null && this.active !== key;
    },

    // The pill keeps the pointer or focus that pressed it, so a severity shown again is the one being looked at.
    toggle(key) {
        this.hidden = { ...this.hidden, [key]: this.isShown(key) };
        this.active = this.isShown(key) ? key : null;
    },

    activate(key) {
        this.active = this.isShown(key) ? key : null;
    },

    deactivate(key) {
        if (this.active === key) {
            this.active = null;
        }
    },

    activePart() {
        return this.parts.find((part) => part.key === this.active) ?? null;
    },

    centreValue() {
        return this.activePart()?.count ?? this.shownTotal;
    },

    centreWords() {
        const part = this.activePart();

        if (part !== null) {
            return words.share.replace(':name', part.name).replace(':percent', `${this.wedges[part.key].share}%`);
        }

        return this.shownTotal === 1 ? words.one : words.other;
    },
});
